<template>
  <b-overlay center :show="isLoading" rounded="sm">
    <div class="d-flex justify-content-center mt-1" v-if="!isLoading">
      <b-calendar
        v-model="inputVal"
        :date-info-fn="dateClass"
        locale="it"
        nav-button-variant="primary"
        @context="onContext"
        @selected="onSelected"
        :hide-header="true"
        start-weekday="1"
        v-bind="labels['it'] || {}"
        :max="max"
      ></b-calendar>
    </div>
    <div class="text-center mt-2" v-if="!isLoading">
      <span v-if="importDatesCount === null">
        Cliccare su una data per conoscere il numero di importazioni di movimenti contabili
      </span>
      <span v-else>
        Numero di importazioni di movimenti contabili per la data selezionata:
        <span class="font-weight-bold">{{ importDatesCount }}</span>
      </span>
    </div>
    <template #overlay>
      <div class="text-center">
        <div class="custom-loader"></div>
        <p class="loading-text">Operazione in corso...</p>
      </div>
    </template>
  </b-overlay>
</template>

<script>
import moment from "moment";
moment.locale("it");
import { RepositoryFactory } from "@/repositories/RepositoryFactory";

export default {
  data() {
    return {
      isLoading: false,
      importDatesRequestId: 0,
      calMonth: null,
      calYear: null,
      actualMonth: null,
      actualYear: null,
      importDates: [],
      importDatesCount: null,
      calDate: null,
      labels: {
        it: {
          labelPrevDecade: "Decade Precedente",
          labelPrevYear: "Anno Precedente",
          labelPrevMonth: "Mese Precedente",
          labelCurrentMonth: "Mese Corrente",
          labelNextMonth: "Mese Successivo",
          labelNextYear: "Anno Successivo",
          labelNextDecade: "Decade Successiva",
          labelToday: "Oggi",
          labelSelected: "",
          labelNoDateSelected: "Nessuna Data Selezionata",
          labelCalendar: "Calendario",
          labelNav: "Navigazione",
          labelHelp: "Usare le frecce per navigare nel calendario",
        },
      },
    };
  },
  props: {
    value: null,
    max: null,
  },
  methods: {
    initDefaultFormValues() {
      this.calMonth = moment().format("MM");
      this.calYear = moment().format("YYYY");
      this.actualMonth = moment().format("MM");
      this.actualYear = moment().format("YYYY");
    },
    onContext(ctx) {
      this.calMonth = moment(ctx.activeYMD, "YYYY-MM-DD").format("MM");
      this.calYear = moment(ctx.activeYMD, "YYYY-MM-DD").format("YYYY");
      let changed =
        this.calMonth !== this.actualMonth || this.calYear !== this.actualYear;
      if (changed) {
        this.inputVal = ctx.activeYMD;
        this.actualMonth = this.calMonth;
        this.actualYear = this.calYear;
        this.fetchImportDates();
      }
    },
    onSelected(ymd /*, date*/) {
      const day = this.importDates.find((item) => item.date === ymd);
      this.importDatesCount = day ? day.count : 0;
    },
    dateClass(ymd) {
      if (ymd.slice(0, 7) !== `${this.calYear}-${this.calMonth}`) {
        return "calendar custom-d-none"; // Hide days from other months
      }
      return this.importDates.map((d) => d.date).includes(ymd)
        ? "calendar has-imports"
        : "calendar free-date";
    },
    fetchImportDates() {
      const requestId = ++this.importDatesRequestId;
      this.isLoading = true;
      let Repo = RepositoryFactory.get("book_entry");
      return Repo.get_import_date(this.calMonth)
        .then((response) => {
          if (requestId === this.importDatesRequestId) {
            this.importDates = response.data.data;
          }
        })
        .catch((error) => {
          if (requestId === this.importDatesRequestId) {
            let errMsg = this.$getErrorMessage(error);
            this.$showSnackbar({ preset: "error", text: errMsg });
          }
        })
        .finally(() => {
          if (requestId === this.importDatesRequestId) {
            this.isLoading = false;
          }
        });
    },
  },
  beforeMount() {
    this.initDefaultFormValues();
  },
  mounted() {
    this.fetchImportDates();
  },
  computed: {
    inputVal: {
      get() {
        return this.calDate;
      },
      set(val) {
        this.calDate = val;
        this.$emit("input", val);
      },
    },
  },
};
</script>

<style scoped lang="scss">
:deep(.calendar) {
  margin: 2px;
  &.has-imports {
    .btn {
      border: 3px solid rgb(13, 227, 13) !important;
    }
  }
  &.free-date {
    .btn {
      border: 2px solid rgb(240, 78, 78) !important;
    }
  }
  &.custom-d-none {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    touch-action: none;
    cursor: default;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
  }
  &:not(.custom-d-none) + .calendar.custom-d-none ~ * {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    touch-action: none;
    cursor: default;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
  }
}

:deep(.b-calendar) {
  .b-calendar-grid {
    box-shadow: none;
    border-color: var(--gray-200);
    gap: 5px;

    .btn.active {
      background-color: var(--lisaweb) !important;
      box-shadow: none !important;
    }

    .btn:not(.active):hover {
      background-color: var(--lisaweb-light) !important;
      box-shadow: none !important;
    }
  }

  .btn {
    background-color: inherit;
    color: var(--color-text);
    font-size: 0.875rem;
    border-radius: 6px;
    padding: 0.3rem 0.5rem;
  }

  &:hover {
    border: none;
  }

  &.active {
    background-color: var(--lisaweb) !important;
    color: white !important;
    box-shadow: 0 0 0 0.15rem var(--lisaweb-light), 0 0rem 0rem 0 rgba(0, 0, 0, 0.075) !important;
  }

  &:focus {
    outline: none !important;
    box-shadow: none !important;
  }

  .btn-outline-primary {
    color: var(--lisaweb) !important;
    box-shadow: 0 0 0 0.15rem var(--lisaweb-light), 0 0rem 0rem 0 rgba(0, 0, 0, 0.075);
  }

  .b-calendar-nav {
    gap: 5px;
  }

  .b-calendar-nav .btn {
    background-color: transparent;
    color: var(--lisaweb);
    height: 30px;
    font-weight: bold;
    box-shadow: none !important;

    &:hover {
      background-color: var(--lisaweb);
      color: white !important;
      box-shadow: none !important;
    }

    &:focus {
      outline: none;
      box-shadow: none !important;
    }
  }
}
</style>
